import React, {memo} from 'react';
import {DownOutline, RightOutline} from 'antd-mobile-icons';
import {Badge} from 'antd-mobile';
import {formatStopNumberFromDeliveryTaskOrder} from '../../../../utils/stopNumberUtils';
import {formatLocalizedDateTime} from '../../../../utils/dateFormatUtils';
import {timestampToDate} from '../../../../utils/dateTimeUtils';
import {getExceptionTypeText, getLocalizedText} from '../../../../utils/i18nUtils';
import {t} from '../../../../i18n';
import {DeliveryTaskOrderType} from '../../graphql/types/DeliveryTaskOrderType';
import {ExceptionDictionaryType, ExceptionSubReasonType} from '../../graphql/queries/getExceptionDictionaryForDelivery';

interface OptimizedOrderItemProps {
    order: DeliveryTaskOrderType;
    isExpanded: boolean;
    isLastItem: boolean;
    exceptionReason?: ExceptionDictionaryType;
    exceptionSubReasons?: ExceptionSubReasonType[];
    onToggle: (orderId: number) => void;
}

const OptimizedOrderItem: React.FC<OptimizedOrderItemProps> = memo(({
    order,
    isExpanded,
    isLastItem,
    exceptionReason,
    exceptionSubReasons,
    onToggle
}) => {
    const stopNumberText = formatStopNumberFromDeliveryTaskOrder(order);
    const trackingNumber = order.orderWithAddress?.order?.trackingNumber || '';
    const deliveryAttempts = order.orderWithAddress?.order?.deliveryAttempts || 0;

    return (
        <div>
            {/* 订单项主行 */}
            <div
                className="flex items-center px-4 py-3 cursor-pointer active:bg-gray-50 border-1"
                onClick={() => onToggle(order.id)}
            >
                <div className="flex-1 flex items-center">
                    <span className="text-2xl mr-2">
                        {stopNumberText}
                    </span>
                    <span className="text-sm text-gray-400">
                        {trackingNumber}
                    </span>
                </div>
                {deliveryAttempts >= 1 && (
                    <Badge content={deliveryAttempts} color="red" className="mr-2"/>
                )}
                {isExpanded ?
                    <DownOutline className="text-gray-400"/> :
                    <RightOutline className="text-gray-400"/>}
            </div>

            {/* 订单详细信息（展开时显示） */}
            {isExpanded && (
                <div className="px-4 pb-3 pt-0 border-t border-gray-100">
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-400">
                                {t('pages.delivery.detail.routeNo')}:
                            </span>
                            <span className="text-gray-800">
                                {order.routeName || '-'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">
                                {t('pages.delivery.detail.assignedTime')}:
                            </span>
                            <span className="text-gray-800">
                                {order.orderWithAddress?.order?.deliverAt
                                    ? formatLocalizedDateTime(timestampToDate(order.orderWithAddress.order.deliverAt))
                                    : '-'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">
                                {t('pages.delivery.detail.deliveryAttempt')}:
                            </span>
                            <span className="text-gray-800">
                                {order.orderWithAddress?.order?.deliveryAttempts || 0} out of 3
                            </span>
                        </div>
                        {/* 异常原因：有二级原因时展示二级，否则展示一级 */}
                        {exceptionReason && (
                            <div className="flex justify-between">
                                <span className="text-gray-400">
                                    {t('pages.delivery.exception.filedReason')}:
                                </span>
                                <span className="text-sm font-medium text-right text-violet-600">
                                    {exceptionSubReasons && exceptionSubReasons.length > 0
                                    ? exceptionSubReasons.map(s => getLocalizedText({ zh: s.textZh, en: s.textEn, es: s.textEs }, s.textEn)).join(', ')
                                    : getExceptionTypeText(exceptionReason)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 分隔线（非最后一项） */}
            {!isLastItem && (
                <div className="border-b border-gray-100 mx-4"/>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    // 自定义比较函数，只有这些属性变化时才重新渲染
    return prevProps.order.id === nextProps.order.id &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.isLastItem === nextProps.isLastItem &&
        prevProps.exceptionReason === nextProps.exceptionReason &&
        prevProps.exceptionSubReasons === nextProps.exceptionSubReasons;
});

OptimizedOrderItem.displayName = 'OptimizedOrderItem';

export default OptimizedOrderItem;
